import { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Eye, Image, Upload, X, Check, FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Style', 'Stories', 'Impact', 'Behind the Scenes'];

// Available images for journal
const AVAILABLE_IMAGES = [
  '/images/products/santorini_collection.jpg',
  '/images/products/santorini_gratitude.jpg',
  '/images/products/desert_sunset_flying.jpg',
  '/images/products/autumn_leaves.jpg',
  '/images/products/beach_with_love.jpg',
  '/images/products/bridal_coffee.jpg',
  '/images/products/artisan_embroidery_woman.jpg',
  '/images/products/artisan_loom_cream.jpg',
  '/images/products/artisan_weaver_man.jpg',
  '/images/products/artisan_yarn_hands.jpg',
  '/images/products/wedding_pillar.jpg',
  '/images/products/beach_sand_white.jpg',
  '/images/products/IMG_6195.jpg',
  '/images/products/IMG_6198.jpg',
  '/images/products/IMG_6270.jpg',
  '/images/products/IMG_6293.jpg',
];

// Image Picker Component
const ImagePicker = ({ selectedImage, onSelect, onClose, token }) => {
  const [allImages, setAllImages] = useState(AVAILABLE_IMAGES);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchUploadedImages();
  }, []);

  const fetchUploadedImages = async () => {
    try {
      const response = await axios.get(`${API}/upload/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const uploadedUrls = response.data.images.map(img => img.url);
      const merged = [...new Set([...AVAILABLE_IMAGES, ...uploadedUrls])];
      setAllImages(merged);
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      onSelect(response.data.url);
      onClose();
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background w-full max-w-3xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <h3 className="text-lg font-medium">Select Featured Image</h3>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Upload option */}
        <div className="p-4 border-b bg-gray-50">
          <label className="flex items-center justify-center gap-3 p-4 border-2 border-dashed border-gray-300 hover:border-primary cursor-pointer transition-colors">
            <Upload className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm">{uploading ? 'Uploading...' : 'Upload new image'}</span>
            <input type="file" className="hidden" accept="image/*" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-4 gap-3">
            {allImages.map((img, idx) => (
              <div
                key={idx}
                onClick={() => { onSelect(img); onClose(); }}
                className={`relative aspect-video cursor-pointer border-2 transition-all hover:border-primary ${
                  selectedImage === img ? 'border-primary ring-2 ring-primary/20' : 'border-transparent'
                }`}
              >
                <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                {selectedImage === img && (
                  <div className="absolute top-1 right-1 w-6 h-6 bg-primary text-white flex items-center justify-center">
                    <Check className="w-4 h-4" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminJournal = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const token = localStorage.getItem('chaleur_admin_token');

  const [form, setForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    category: 'Stories',
    author: 'Chaleur Editorial',
    tags: [],
    is_published: true
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/journal`, { params: { limit: 100 } });
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPost) {
        // Update existing post
        await axios.put(`${API}/journal/${editingPost.slug}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post updated');
      } else {
        // Create new post
        await axios.post(`${API}/journal`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post created');
      }
      setShowForm(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setForm({
      title: post.title || '',
      slug: post.slug || '',
      excerpt: post.excerpt || '',
      content: post.content || '',
      featured_image: post.featured_image || '',
      category: post.category || 'Stories',
      author: post.author || 'Chaleur Editorial',
      tags: post.tags || [],
      is_published: post.is_published !== false
    });
    setShowForm(true);
  };

  const handleDelete = async (slug) => {
    try {
      await axios.delete(`${API}/journal/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      setDeleteConfirm(null);
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      featured_image: '',
      category: 'Stories',
      author: 'Chaleur Editorial',
      tags: [],
      is_published: true
    });
    setEditingPost(null);
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  return (
    <div className="space-y-6" data-testid="admin-journal">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-light mb-2">Journal</h1>
          <p className="text-muted-foreground">Manage blog posts and articles ({posts.length} total)</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowForm(true); }}
          className="rounded-none bg-foreground text-primary-foreground"
        >
          <Plus className="w-4 h-4 mr-2" /> New Post
        </Button>
      </div>

      {/* Posts Table */}
      <Card className="rounded-none border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
            </div>
          ) : posts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left py-3 px-4 text-sm font-medium">Image</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Title</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Category</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map(post => (
                    <tr key={post.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-4">
                        <div className="w-16 h-12 bg-gray-100 overflow-hidden">
                          {post.featured_image ? (
                            <img src={post.featured_image} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <Image className="w-5 h-5" />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-medium block">{post.title}</span>
                        <span className="text-xs text-muted-foreground">{post.slug}</span>
                      </td>
                      <td className="py-3 px-4 text-sm">{post.category}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs ${
                          post.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {post.is_published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-muted-foreground">
                        {new Date(post.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleEdit(post)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setDeleteConfirm(post)}
                            className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <a 
                            href={`/journal/${post.slug}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="h-8 w-8 p-0 inline-flex items-center justify-center hover:bg-gray-100 rounded"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No posts yet. Create your first journal entry.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="rounded-none max-w-md">
            <DialogHeader>
              <DialogTitle>Delete Post</DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground">
              Are you sure you want to delete "{deleteConfirm.title}"? This action cannot be undone.
            </p>
            <div className="flex gap-4 justify-end mt-4">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} className="rounded-none">
                Cancel
              </Button>
              <Button 
                onClick={() => handleDelete(deleteConfirm.slug)} 
                className="rounded-none bg-red-600 hover:bg-red-700 text-white"
              >
                Delete
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Post Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-none">
          <DialogHeader>
            <DialogTitle>{editingPost ? 'Edit Post' : 'New Post'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ 
                    ...form, 
                    title: e.target.value, 
                    slug: editingPost ? form.slug : generateSlug(e.target.value) 
                  })}
                  className="rounded-none"
                  required
                />
              </div>
              <div>
                <Label className="mb-2 block">Slug</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="rounded-none"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-none">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">Author</Label>
                <Input
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                  className="rounded-none"
                />
              </div>
            </div>

            {/* Featured Image with Preview */}
            <div>
              <Label className="mb-2 block">Featured Image</Label>
              <div className="flex gap-4 items-start">
                <div className="flex-1">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowImagePicker(true)}
                    className="w-full rounded-none justify-start"
                  >
                    <FolderOpen className="w-4 h-4 mr-2" />
                    {form.featured_image ? 'Change Image' : 'Select Image'}
                  </Button>
                </div>
                {form.featured_image && (
                  <div className="relative w-32 h-20 bg-gray-100 group">
                    <img src={form.featured_image} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, featured_image: '' })}
                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="mb-2 block">Excerpt *</Label>
              <Textarea
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                className="min-h-[80px] rounded-none"
                placeholder="Brief summary of the article..."
                required
              />
            </div>

            <div>
              <Label className="mb-2 block">Content (HTML supported) *</Label>
              <Textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                className="min-h-[250px] rounded-none font-mono text-sm"
                placeholder="<p>Your article content here...</p>"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                Supports HTML tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;em&gt;
              </p>
            </div>

            <label className="flex items-center gap-2">
              <Checkbox 
                checked={form.is_published}
                onCheckedChange={(c) => setForm({ ...form, is_published: c })}
              />
              Publish immediately
            </label>

            <div className="flex gap-4 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-none">
                Cancel
              </Button>
              <Button type="submit" className="rounded-none bg-foreground text-primary-foreground">
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Image Picker */}
      {showImagePicker && (
        <ImagePicker
          selectedImage={form.featured_image}
          onSelect={(url) => setForm({ ...form, featured_image: url })}
          onClose={() => setShowImagePicker(false)}
          token={token}
        />
      )}
    </div>
  );
};

export default AdminJournal;
